class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    const progressLine = document.querySelector('.cart-progress-bar__line');
    const currentProgress = progressLine ? 
      getComputedStyle(progressLine).getPropertyValue('--line-progress').trim() : '0%';

    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    animateProgressBar(currentProgress);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerProductRecommendations extends HTMLElement {
  constructor() {
    super();
    
    this.loadRecommendations = this.loadRecommendations.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
    
    this.observer = new IntersectionObserver(this.handleIntersection, {
      rootMargin: '0px 0px 200px 0px',
    });
    
    this.observer.observe(this);
  }

  connectedCallback() {
    if (this.observer && !this.isObserved) {
      this.observer.observe(this);
      this.isObserved = true;
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.unobserve(this);
      this.observer.disconnect();
    }
  }

  handleIntersection(entries) {
    if (!entries[0].isIntersecting) return;
    this.observer.unobserve(this);
    this.loadRecommendations();
  }

  async loadRecommendations() {
    // If upsell slider markup is already present, initialize it and skip fetching
    const root = this.closest('#CartDrawerRecommendations') || this;
    const upsellSwiper = root.querySelector('.cart-product-recommendations--swiper-upsell');
    if (upsellSwiper) {
      this.initializeSwiper(upsellSwiper);
      return;
    }

    // Otherwise fetch <product-recommendations>
    const parentContainer = this.closest('[data-url]');
    const url = parentContainer?.dataset?.url;

    if (!url) {
      this.hideSection();
      return;
    }

    this.showLoading();
    
    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      this.renderRecommendations(text);
    } catch (error) {
      console.error('Error loading recommendations:', error);
      this.hideSection();
    }
  }

  renderRecommendations(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const recommendations = doc.querySelector('product-recommendations');
    
    if (recommendations && recommendations.innerHTML.trim()) {
      this.innerHTML = recommendations.innerHTML;
      this.initializeSwiper();
    } else {
      this.hideSection();
    }
  }

  initializeSwiper(swiperContainer) {
    const target = swiperContainer || this.querySelector('.cart-product-recommendations--swiper, .cart-product-recommendations--swiper-upsell');
    if (!target) return;
    if (target.classList.contains('swiper-initialized') || target.swiper) return;

    // Ensure Swiper library is available
    if (typeof Swiper === 'undefined') {
      console.warn('Swiper is not available on window. Skipping initialization.');
      return;
    }

    requestAnimationFrame(() => {
      new Swiper(target, {
        slidesPerView: 2.2,
        spaceBetween: 15,
        loop: false,
        observer: true,
        observerParents: true,
        breakpoints: {
          600: {
            slidesPerView: 2.5,
            spaceBetween: 15,
          },
        },
      });
    });
  }

  showLoading() {
    this.innerHTML = '<div class="cart__recommendations--loading" aria-live="polite">Loading recommendations...</div>';
  }

  hideSection() {
    const container = this.closest('#CartDrawerRecommendations');
    if (container) {
      container.classList.add('hidden');
    }
  }
}
customElements.define('cart-drawer-recommendations', CartDrawerProductRecommendations);
